import React, { useId, useMemo } from "react";
import { can, readSession } from "../utils/organisationApi";
import { announcePlanBlocked } from "../utils/planEvents";
import { blockedMessage, evaluateGate, usePlan } from "../utils/subscriptionData";

// Entitlement check for one control. `metric` is a usage key such as "job_slots"; leave it out when
// the action only needs an active plan. The backend enforces the same rules; this just explains them first.
//
//   const gate = usePlanGate("job_slots");
//   <Button {...gate.controlProps} onClick={gate.guard(openModal)}>Add Job</Button>{gate.hint}
//
// A blocked control looks and works like any other: it is not greyed out. Activating it opens the plan dialog
// (Upgrade or Buy plan) instead of the action, and the reason is available to screen readers.
const usePlanGate = (metric = null, count = 1) => {
  const { data } = usePlan();
  const hintId = useId();
  const canBill = useMemo(() => can(readSession(), "manageBilling"), []);
  const result = evaluateGate(data, metric, count);
  const blocked = !result.allowed;
  const message = blocked ? blockedMessage({ ...result, canBill }) : "";

  const guard = (action) => (event) => {
    if (!blocked) return action?.(event);
    event?.preventDefault?.();
    announcePlanBlocked({ code: result.reason === "no_plan" ? "NO_ACTIVE_PLAN" : "PLAN_LIMIT_REACHED", message, metric: result.metric || null });
    return undefined;
  };

  return {
    blocked,
    message,
    canBill,
    guard,
    controlProps: blocked ? { "aria-describedby": hintId } : {},
    hint: blocked ? (
      <span id={hintId} className="sr-only">
        {message}
      </span>
    ) : null,
  };
};

export default usePlanGate;
